/**
 * The things a parent records over and over.
 *
 * "Tidied the kitchen, two dollars, Ravi and Susan" is not a form worth
 * filling in every Saturday. A quick action is that form, saved, as one button.
 *
 * Pressing one writes an ordinary transaction -- same ledger, same split, same
 * void behaviour. Nothing about a quick action is a different KIND of money;
 * it is a shortcut to the same write, which is why the entry it produces is
 * indistinguishable from one typed by hand.
 */

import { useState } from 'react';
import {
  removeQuickAction,
  saveQuickAction,
  type Person,
  type QuickAction,
} from '../family/state';
import { amountInputMode, amountPlaceholder, asTyped, dollars, howMuch, looksLikeAmount, signed } from '../family/money';

export function QuickActions({
  familyId,
  familyKey,
  actions,
  people,
  busy,
  onUse,
  onChanged,
}: {
  familyId: string;
  familyKey: CryptoKey;
  actions: QuickAction[];
  people: Person[];
  busy: boolean;
  onUse: (entry: { amount: string; reason: string; people: string[] }) => void;
  onChanged: (work: () => Promise<void>) => void;
}) {
  const [managing, setManaging] = useState(false);
  const [adding, setAdding] = useState(false);

  if (actions.length === 0 && !adding) {
    return (
      <button type="button" className="text-button" onClick={() => setAdding(true)}>
        Add a shortcut
      </button>
    );
  }

  return (
    <>
      {/* No label of its own: the section it sits in is headed "Shortcuts". */}
      <div className="chips">
        {actions.map((action) => (
          <span className="quick" key={action.id}>
            <button
              type="button"
              className="chip"
              disabled={busy}
              onClick={() =>
                onUse({
                  // The stored amount is already signed: a shortcut for
                  // spending was saved negative. Turning it back into the
                  // string a parent would have typed keeps every amount in
                  // this app on one path to the server.
                  amount: asTyped(action.amount),
                  reason: action.reason,
                  people: action.people,
                })
              }
            >
              {action.reason || 'Unnamed'} {dollars(action.amount)}
            </button>
            {managing ? (
              <button
                type="button"
                className="text-button"
                disabled={busy}
                onClick={() => onChanged(() => removeQuickAction(familyId, action.id))}
                aria-label={`Remove ${action.reason}`}
              >
                &times;
              </button>
            ) : null}
          </span>
        ))}
      </div>

      {adding ? (
        <QuickForm
          people={people}
          busy={busy}
          onCancel={() => setAdding(false)}
          onSave={(values) =>
            onChanged(async () => {
              await saveQuickAction(familyId, familyKey, values);
              setAdding(false);
            })
          }
        />
      ) : (
        <div className="secret-actions">
          <button type="button" className="text-button" onClick={() => setAdding(true)}>
            Add a shortcut
          </button>
          <button
            type="button"
            className="text-button"
            onClick={() => setManaging((current) => !current)}
          >
            {managing ? 'Done' : 'Edit shortcuts'}
          </button>
        </div>
      )}
    </>
  );
}

function QuickForm({
  people,
  busy,
  onSave,
  onCancel,
}: {
  people: Person[];
  busy: boolean;
  onSave: (values: { reason: string; amount: string; people: string[] }) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [chosen, setChosen] = useState<string[]>([]);

  const ready = reason.trim() !== '' && looksLikeAmount(amount);

  return (
    <div className="record">
      <div className="segmented">
        <button
          type="button"
          className={direction === 'in' ? 'on' : ''}
          onClick={() => setDirection('in')}
        >
          Money in
        </button>
        <button
          type="button"
          className={direction === 'out' ? 'on' : ''}
          onClick={() => setDirection('out')}
        >
          Spent
        </button>
      </div>

      <label className="field">
        <span>What is it?</span>
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={80}
          autoComplete="off"
          placeholder="Tidied the kitchen"
        />
        <small>Encrypted before it leaves this device.</small>
      </label>

      <label className="field">
        <span>{howMuch()}</span>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          inputMode={amountInputMode()}
          autoComplete="off"
          placeholder={amountPlaceholder()}
        />
      </label>

      {people.length ? (
        <>
          <p className="field-label">Who is it usually for?</p>
          <div className="chips">
            {people.map((person) => (
              <button
                type="button"
                key={person.id}
                className={chosen.includes(person.id) ? 'chip on' : 'chip'}
                onClick={() =>
                  setChosen((current) =>
                    current.includes(person.id)
                      ? current.filter((one) => one !== person.id)
                      : [...current, person.id],
                  )
                }
              >
                {person.name || 'Unnamed'}
              </button>
            ))}
          </div>
        </>
      ) : null}

      <div className="actions">
        <button aria-busy={busy}
          className="primary"
          type="button"
          disabled={busy || !ready}
          onClick={() =>
            onSave({
              reason: reason.trim(),
              amount: signed(amount, direction),
              people: chosen,
            })
          }
        >
          {busy ? 'Saving…' : 'Save the shortcut'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
